import { io } from "socket.io-client";

// Signals can be replayed by a flaky connection. Remember just enough ids to
// drop the repeats.
const SEEN_LIMIT = 50;

// Swap the token this long before it expires rather than waiting for the server
// to refuse it: Socket.IO reuses the original auth object on every reconnect, so
// a token that dies mid-shift would retry with it forever.
const REFRESH_MARGIN_MS = 60 * 1000;

// A renewal that fails backs off to this instead of retrying against an expiry
// already in the past, which would ask the token endpoint once a second.
const RENEW_RETRY_MS = 30 * 1000;

// Renewals that keep failing double that wait up to this. A token endpoint that
// keeps answering "no" without saying realtime is off would otherwise be asked
// every 30 s for the rest of the shift, by every board in every outlet.
const RENEW_RETRY_MAX_MS = 5 * 60 * 1000;

// After this many REJECTED tokens in a row, stop pretending realtime works and
// let the board fall back to its poll. Only a token the server refused counts —
// see _handleConnectError.
const MAX_AUTH_FAILURES = 3;

// `renewing` hides a renewal's own disconnect/connect pair from the caller. If
// the reconnect never lands, that concealment has to end by itself, or the board
// believes it has a live socket, keeps its slow heartbeat, and goes quiet on
// both channels at once.
const RENEW_BLIP_MAX_MS = 15 * 1000;

/**
 * One board's realtime channel.
 *
 * The server sends a signal ("this board moved"), never the board itself, so
 * `onSignal` means "re-read the board from the API". A board is a filtered query
 * over many orders; there is nothing in the signal to compare against what the
 * screen holds, so every signal is worth one read.
 *
 * `onConnectionChange` exists so the screen can keep its poll and slow it down
 * while the socket is actually up. It can never stop polling entirely — that
 * poll is also what renews the stock passcode token.
 */
export default class BoardRealtime {
  /**
   * @param {object} options
   * @param {Function} options.fetchToken  async () => ({ enabled, token, url, expires_in })
   * @param {Function} options.onSignal    called when the board changed, and on every (re)connect
   * @param {Function} options.onConnectionChange called with true/false
   */
  constructor({ fetchToken, onSignal, onConnectionChange }) {
    this.fetchToken = fetchToken;
    this.onSignal = onSignal || (() => {});
    this.onConnectionChange = onConnectionChange || (() => {});

    this.socket = null;
    this.seen = [];
    this.expiresAtMs = 0;
    this.authFailures = 0;
    this.stopped = false;
    this.renewTimer = null;
    this.renewFailures = 0;
    this.renewing = false;
    this.renewBlipTimer = null;
    this.refreshInFlight = null;
    this._resumeHandler = null;
    this.hasConnectedOnce = false;
  }

  async start() {
    let issued;

    try {
      issued = await this.fetchToken();
    } catch (err) {
      // Realtime is an optimisation. A failure here is not a page error.
      return false;
    }

    if (this.stopped || !issued || !issued.enabled || !issued.token || !issued.url) {
      return false;
    }

    // A duration from now rather than the server's absolute expiry: a kitchen
    // tablet's clock and the server's disagree often enough that a renewal aimed
    // at the wrong instant either fires constantly or never fires at all.
    this.expiresAtMs = Date.now() + (issued.expires_in || 0) * 1000;

    this.socket = io(issued.url, {
      transports: ["websocket"],
      auth: { token: issued.token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      timeout: 10000,
    });

    // connection.ready lands on the first connect AND on every reconnect, which
    // makes it the natural resync point: anything missed while the socket was
    // down is picked up by the re-read this triggers.
    this.socket.on("connection.ready", () => {
      const isReconnect = this.hasConnectedOnce;

      this.hasConnectedOnce = true;
      this.authFailures = 0;
      this.renewFailures = 0;
      this._endRenewBlip();
      this._scheduleRenewal();
      this.onConnectionChange(true);

      // The FIRST connection.ready is not a resync.
      //
      // A resync exists to catch what was missed while the socket was down. On a
      // reconnect that is a real gap. On the first connect it is not: the screen
      // loaded its data moments earlier as part of the same startup, and asking
      // it to read again doubles the requests of every single page load.
      //
      // What is left uncovered is the gap between that initial read returning and
      // this handshake completing — a few hundred milliseconds. A change landing
      // inside it waits for the next signal or the fallback timer, which is the
      // same thing that already covers a signal dropped in flight.
      if (isReconnect) {
        this.onSignal({ reason: "reconnected", resync: true });
      }
    });

    this.socket.on("board.updated", (payload) => {
      if (this._isRepeat(payload)) return;
      this.onSignal(payload && payload.data ? payload.data : {});
    });

    this.socket.on("disconnect", () => {
      if (this.renewing) return;
      this.onConnectionChange(false);
    });

    // The server cuts a socket loose once its token passes exp. Reaching this
    // means the renewal below did not get there first — a tablet whose timers
    // were throttled, most likely.
    this.socket.on("token.expired", () => {
      this._renewToken();
    });

    this.socket.on("connect_error", (err) => {
      this._endRenewBlip();
      this.onConnectionChange(false);
      this._handleConnectError(err);
    });

    this.socket.on("server.shutdown", () => {
      this.onConnectionChange(false);
    });

    this.socket.io.on("reconnect_attempt", () => {
      if (Date.now() > this.expiresAtMs - REFRESH_MARGIN_MS) {
        this._refreshToken();
      }
    });

    this._watchForResume();

    return true;
  }

  /**
   * Notice when the device starts running our code again.
   *
   * Kept here rather than in each board screen for two reasons. It is the same
   * handling every one of them needs, and it is this class that knows what to do
   * about it — the screens only learn "you are disconnected" and "re-read", which
   * they already handle.
   */
  _watchForResume() {
    if (typeof document === "undefined" || typeof window === "undefined") return;

    this._resumeHandler = () => {
      if (this.stopped) return;
      if (document.visibilityState === "hidden") return;

      // Correct a stale "connected" before anything else. A suspended tab's
      // socket is torn down by the OS without either side getting to say so, so
      // the screen can be sitting on a five-minute heartbeat believing it has a
      // live push channel — blind on both channels at once.
      if (!this.isConnected()) this.onConnectionChange(false);

      this.wake();

      // Whatever arrived while the device was asleep was never delivered
      // anywhere. `resync: false` so the screen reads promptly rather than
      // spreading it: a device waking is one screen, not the whole estate
      // reconnecting to a restarted server.
      this.onSignal({ reason: "resumed", resync: false });
    };

    document.addEventListener("visibilitychange", this._resumeHandler);
    window.addEventListener("online", this._resumeHandler);
  }

  _stopWatchingForResume() {
    if (!this._resumeHandler) return;

    document.removeEventListener("visibilitychange", this._resumeHandler);
    window.removeEventListener("online", this._resumeHandler);
    this._resumeHandler = null;
  }

  /**
   * Bring the socket back after the device stopped running our code.
   *
   * Kitchen screens are meant to stay awake, but tablets sleep, browsers
   * background tabs, and nothing in socket.io's reconnect logic runs while that
   * happens. What it resumes into is a backoff that may be a full delay away,
   * holding a token that expired meanwhile.
   */
  wake() {
    if (this.stopped || !this.socket) return;

    if (Date.now() > this.expiresAtMs - REFRESH_MARGIN_MS) {
      this._renewToken();
      return;
    }

    if (!this.socket.connected) this.socket.connect();
  }

  isConnected() {
    return Boolean(this.socket && this.socket.connected);
  }

  stop() {
    this.stopped = true;

    this._stopWatchingForResume();

    clearTimeout(this.renewTimer);
    this.renewTimer = null;
    clearTimeout(this.renewBlipTimer);
    this.renewBlipTimer = null;
    this.refreshInFlight = null;

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.io.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.onConnectionChange(false);
  }

  _isRepeat(payload) {
    const id = payload && payload.eventId;
    if (!id) return false;
    if (this.seen.includes(id)) return true;

    this.seen.push(id);
    if (this.seen.length > SEEN_LIMIT) this.seen.shift();

    return false;
  }

  async _handleConnectError(err) {
    const code = err && err.data && err.data.code;

    // TOKEN_EXPIRED is routine; TOKEN_INVALID happens once if the shared secret
    // was rotated. Everything else (CLAIM_INVALID) is a bug on our side that a
    // new token will not fix.
    if (code !== "TOKEN_EXPIRED" && code !== "TOKEN_INVALID") return;

    // Order matters: a failure is only counted once a NEW token has been
    // refused. Counting the attempt first means an outlet that loses its
    // connection for half an hour retries with the token it already has —
    // because the refresh cannot reach the API either — collects three
    // TOKEN_EXPIREDs, and shuts realtime down for the rest of the shift.
    if (!(await this._refreshToken())) {
      // The server already closed this handshake and socket.io will not retry
      // it, so nothing else would bring the socket back before a visibility or
      // online event. Hand it to the renewal, which reconnects once a token
      // comes through.
      this._scheduleRenewRetry();
      return;
    }

    this.authFailures += 1;

    if (this.authFailures > MAX_AUTH_FAILURES) {
      this.stop();
      return;
    }

    // A refusal from the server's auth middleware is final as far as
    // socket.io-client is concerned: it does not reconnect on its own, so the
    // new token sits in socket.auth unused unless the handshake is started here.
    // Not while one is already under way: a second CONNECT on top of it would
    // be a second handshake for the same socket.
    if (
      !this.stopped &&
      this.socket &&
      !this.socket.connected &&
      !this.socket.active
    ) {
      this.socket.connect();
    }
  }

  /**
   * Swap in a fresh token, at most one request at a time.
   *
   * Two callers race on every failed reconnect: `reconnect_attempt` asks before
   * the attempt, and the `connect_error` it produces asks again. Against an
   * unreachable server socket.io retries forever, so without this every board in
   * every outlet would put two token requests a second onto the API for as long
   * as the outage lasted.
   */
  async _refreshToken() {
    if (this.stopped || !this.socket) return false;

    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      try {
        const issued = await this.fetchToken();
        if (this.stopped || !this.socket) return false;

        if (issued && issued.enabled && issued.token) {
          this.expiresAtMs = Date.now() + (issued.expires_in || 0) * 1000;
          this.socket.auth = { token: issued.token };
          return true;
        }

        // A definite no from the server: realtime switched off, or this
        // merchant taken off the rollout list. Not a failure to retry — before
        // this the renewal asked again every 30 s and socket.io kept
        // reconnecting with the dead token, forever. stop() reports the socket
        // as gone, so the board falls back to its normal poll; a reload picks
        // realtime up again if the merchant is switched back on.
        if (issued && issued.enabled === false) {
          this.stop();
        }
      } catch (err) {
        // Leave the old token in place; socket.io keeps retrying on its own.
      }

      return false;
    })();

    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  _scheduleRenewal(delayMs) {
    clearTimeout(this.renewTimer);

    if (this.stopped) return;
    if (delayMs === undefined && !this.expiresAtMs) return;

    const delay =
      delayMs === undefined
        ? Math.max(1000, this.expiresAtMs - REFRESH_MARGIN_MS - Date.now())
        : delayMs;

    this.renewTimer = setTimeout(() => this._renewToken(), delay);
  }

  // Retry a failed renewal on the 30 s → 5 min backoff.
  _scheduleRenewRetry() {
    this._scheduleRenewal(
      Math.min(RENEW_RETRY_MS * 2 ** this.renewFailures, RENEW_RETRY_MAX_MS)
    );
    this.renewFailures += 1;
  }

  async _renewToken() {
    if (this.stopped || !this.socket) return;

    if (!(await this._refreshToken())) {
      this._scheduleRenewRetry();
      return;
    }

    this.renewFailures = 0;

    if (this.stopped || !this.socket) return;

    // socket.io authenticates at the handshake and nowhere else, so a new token
    // only takes effect on a new handshake. Doing that here while the old token
    // is still valid costs milliseconds; waiting for the server to close the
    // socket at exp costs a reconnect carrying a dead token.
    this._beginRenewBlip();
    this.socket.disconnect().connect();
  }

  _beginRenewBlip() {
    this.renewing = true;
    clearTimeout(this.renewBlipTimer);

    this.renewBlipTimer = setTimeout(() => {
      if (!this.renewing) return;
      this.renewing = false;
      this.onConnectionChange(this.isConnected());
    }, RENEW_BLIP_MAX_MS);
  }

  _endRenewBlip() {
    this.renewing = false;
    clearTimeout(this.renewBlipTimer);
    this.renewBlipTimer = null;
  }
}
